/**
 * Learn a language — turn-based conversational practice ("Learn" tab).
 *
 * Flow: setup (target language, level, scenario card) → practice (spoken
 * roleplay with an AI partner: target-script + transliteration + tap-to-
 * reveal English, per-turn feedback, help/skip escape hatches) → recap
 * (stats + phrase bank).
 *
 * Server dependency: `POST /learn/turn` (see api.LearnTurnRequest). Until
 * that endpoint ships, the screen degrades to a friendly "not available on
 * this server yet" banner after setup.
 *
 * Deliberate design choices (see design mockup / memory.md):
 *   - Turn-based over REST, not the live WS pipeline — single speaker, no
 *     diarization, radically simpler.
 *   - Feedback is attached to the learner's bubble, two lines max, framed
 *     around comprehensibility ("Ravi understood you"), never scores.
 *   - "Gentle" level = English shown by default; "Normal" hides it behind
 *     a tap (retrieval effort is the difficulty slider).
 */
import { useEffect, useRef, useState } from "react";
import * as api from "../api";
import { authStore } from "../store";
import { Btn, Dots, Modal, LangList, useLanguages, langByCode } from "../ui";
import { Icon } from "../icons";
import { pcm16ToWavBlob } from "../audio/mic";
import { VadRecorder } from "../audio/recorder";
import { TtsPlayer } from "../live/tts";
import { base64ToArrayBuffer } from "../live/engine";

const ANSWER_MAX_SEC = 12;
const SILENCE_HANG_FRAMES = 42; // ~1.34 s
// Learner attempts can legitimately be short ("oka kilo") — unlike
// enrollment, don't force long answers; ~0.5 s of voice is enough.
const MIN_VOICED_FRAMES = 16;

// Culture-neutral archetypes: the server's tutor LLM localises the persona
// (name + setting) to the target language — Telugu gets Ravi at a vegetable
// market, French gets Marie at the marché — so titles here stay generic.
// `roleShort` labels bubbles until the LLM's chosen name arrives.
type Scenario = {
  id: string;
  icon: string;
  title: string;
  sub: string;
  roleShort: string;
  roleDesc: string;
};

const SCENARIOS: Scenario[] = [
  { id: "in_laws", icon: "user", title: "Meeting the in-laws", sub: "Warm small talk", roleShort: "Host", roleDesc: "your host" },
  { id: "market", icon: "store", title: "At the market", sub: "Numbers, bargaining", roleShort: "Seller", roleDesc: "seller" },
  { id: "rickshaw", icon: "car", title: "Ride across town", sub: "Directions, fares", roleShort: "Driver", roleDesc: "driver" },
  { id: "tea_stall", icon: "cup", title: "Café chat", sub: "Everyday phrases", roleShort: "Owner", roleDesc: "café owner" },
];

type PartnerTurn = {
  id: string; who: "partner";
  text: string; translit: string; en: string;
  showEn: boolean; audioB64: string | null;
  /** Placeholder shown while the server round-trip is in flight. */
  pending?: boolean;
};
type LearnerTurn = {
  id: string; who: "learner";
  text: string; translit: string; meaning: string;
  feedback: api.LearnTurnFeedback | null;
  pending?: boolean;
};
type Turn = PartnerTurn | LearnerTurn;

type Phase = "idle" | "thinking" | "partner_speaking" | "listening";
type Step = "setup" | "practice" | "recap";

export function LearnScreen() {
  const user = authStore((s) => s.user);
  const [step, setStep] = useState<Step>("setup");
  const [target, setTarget] = useState(() => user?.other_lang_override ?? user?.fallback_other_lang ?? "te-IN");
  const [level, setLevel] = useState<api.LearnLevel>("gentle");
  const [scenario, setScenario] = useState<Scenario>(SCENARIOS[0]!);
  const [session, setSession] = useState(0); // bump to remount Practice

  const native = user?.owner_lang ?? "en-US";
  const langs = useLanguages();

  // Opener pre-warm (demo trick): while the user is still reading the setup
  // screen, fetch the scenario's opening line so tapping "Start talking"
  // plays instantly instead of eating the full LLM+TTS round trip. Keyed by
  // the current selection; re-fired (debounced) when the selection changes.
  const openerKey = `${scenario.id}|${level}|${target}|${native}`;
  const openerRef = useRef<{ key: string; handle: api.LearnTurnHandle } | null>(null);
  useEffect(() => {
    if (step !== "setup") return;
    const key = openerKey;
    const t = setTimeout(() => {
      // Abandon any earlier un-taken opener before pre-warming a new one.
      openerRef.current?.handle.abort();
      const handle = api.learnTurn({
        scenario_id: scenario.id, level,
        native_language: native, target_language: target, history: [],
      });
      openerRef.current = { key, handle };
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, openerKey]);

  // Recap payload handed from Practice on finish.
  const [recap, setRecap] = useState<{ turns: number; talkSec: number; phrases: { text: string; translit: string }[] }>({ turns: 0, talkSec: 0, phrases: [] });

  if (step === "practice") {
    return (
      <Practice
        key={session}
        native={native} target={target} level={level} scenario={scenario}
        takeOpener={() => {
          const cur = openerRef.current;
          openerRef.current = null;
          if (cur && cur.key === openerKey) return cur.handle;
          cur?.handle.abort(); // stale selection — cancel the pre-warm
          return null;
        }}
        onExit={(r) => { setRecap(r); setStep(r.turns > 0 ? "recap" : "setup"); }}
      />
    );
  }

  if (step === "recap") {
    return (
      <Recap
        recap={recap} target={target} langs={langs}
        onAgain={() => { setSession((n) => n + 1); setStep("practice"); }}
        onHarder={() => { setLevel("normal"); setSession((n) => n + 1); setStep("practice"); }}
        onScenes={() => setStep("setup")}
      />
    );
  }

  return (
    <Setup
      native={native} target={target} setTarget={setTarget}
      level={level} setLevel={setLevel}
      scenario={scenario} setScenario={setScenario}
      langs={langs}
      onStart={() => { setSession((n) => n + 1); setStep("practice"); }}
    />
  );
}

// ─── Setup ────────────────────────────────────────────────────────────────

function Setup({
  native, target, setTarget, level, setLevel, scenario, setScenario, langs, onStart,
}: {
  native: string; target: string; setTarget: (c: string) => void;
  level: api.LearnLevel; setLevel: (l: api.LearnLevel) => void;
  scenario: Scenario; setScenario: (s: Scenario) => void;
  langs: ReturnType<typeof useLanguages>;
  onStart: () => void;
}) {
  const [pick, setPick] = useState(false);
  const [query, setQuery] = useState("");
  const nativeLang = langByCode(native, langs);
  const targetLang = langByCode(target, langs);

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 28px 60px" }}>
      <h1 className="display" style={{ fontSize: 28, fontWeight: 700, color: "var(--ink)", margin: 0, letterSpacing: -0.5 }}>
        Learn a language
      </h1>
      <p style={{ color: "var(--ink-3)", marginTop: 6, fontSize: 14.5, marginBottom: 22 }}>
        Short spoken practice, like a real chat.
      </p>

      <button onClick={() => setPick(true)} style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: "var(--r-pill)", padding: "11px 16px", cursor: "pointer",
        fontFamily: "var(--font-ui)", fontSize: 14.5, color: "var(--ink)", marginBottom: 14,
      }}>
        <span style={{ color: "var(--ink-3)" }}>I speak</span>
        <span style={{ fontWeight: 700 }}>{nativeLang.name}</span>
        <Icon name="arrowRight" size={14} color="var(--ink-faint)" />
        <span style={{ color: "var(--ink-3)" }}>learning</span>
        <span style={{ fontWeight: 700, color: "var(--accent-ink)" }}>{targetLang.name}</span>
        <Icon name="chevronDown" size={14} color="var(--ink-3)" style={{ marginLeft: "auto" }} />
      </button>

      <div style={{ display: "inline-flex", gap: 4, background: "var(--surface-2)", borderRadius: "var(--r-pill)", padding: 4, marginBottom: 20 }}>
        {(["gentle", "normal"] as const).map((l) => (
          <button key={l} onClick={() => setLevel(l)} style={{
            border: "none", cursor: "pointer", borderRadius: "var(--r-pill)", padding: "6px 18px",
            fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 700, textTransform: "capitalize",
            background: level === l ? "var(--accent)" : "transparent",
            color: level === l ? "var(--on-accent)" : "var(--ink-3)",
          }}>{l}</button>
        ))}
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)", marginBottom: 9 }}>Pick a scene</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
        {SCENARIOS.map((s) => {
          const active = s.id === scenario.id;
          return (
            <button key={s.id} onClick={() => setScenario(s)} style={{
              textAlign: "left", cursor: "pointer", borderRadius: 16, padding: "14px 15px",
              background: "var(--surface)", fontFamily: "var(--font-ui)",
              border: `1.5px solid ${active ? "var(--accent)" : "var(--border)"}`,
            }}>
              <Icon name={s.icon} size={19} color={active ? "var(--accent)" : "var(--ink-3)"} />
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginTop: 7 }}>{s.title}</div>
              <div style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 2 }}>{s.sub}</div>
            </button>
          );
        })}
      </div>

      <Btn size="lg" icon="mic" onClick={onStart}>Start talking</Btn>

      <Modal open={pick} onClose={() => { setPick(false); setQuery(""); }} title="Learn which language?" sub="Pick the language you want to practice.">
        <LangList activeCode={target} who="guest" query={query} setQuery={setQuery} showRecents
          onPick={(code) => { setTarget(code); setPick(false); setQuery(""); }} />
      </Modal>
    </div>
  );
}

// ─── Practice ─────────────────────────────────────────────────────────────

function Practice({
  native, target, level, scenario, takeOpener, onExit,
}: {
  native: string; target: string; level: api.LearnLevel; scenario: Scenario;
  takeOpener?: () => api.LearnTurnHandle | null;
  onExit: (recap: { turns: number; talkSec: number; phrases: { text: string; translit: string }[] }) => void;
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [phase, setPhaseState] = useState<Phase>("idle");
  const [paused, setPausedState] = useState(false);
  // "You could say…" hint for the learner's next turn. Gentle level shows
  // it automatically; normal level keeps it behind a "Hint" tap so the
  // retrieval effort stays.
  const [hint, setHint] = useState<api.LearnSuggestion | null>(null);
  const [hintShown, setHintShown] = useState(false);
  // LLM-chosen, culture-appropriate partner name ("Ravi", "Marie", …);
  // role label stands in until the opener response delivers it.
  const [personaName, setPersonaName] = useState<string | null>(null);
  const personaRef = useRef<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const langs = useLanguages();

  const cancelledRef = useRef(false);
  const recorderRef = useRef<VadRecorder | null>(null);
  const playerRef = useRef<TtsPlayer | null>(null);
  const turnsRef = useRef<Turn[]>([]);
  const talkSecRef = useRef(0);
  const phaseRef = useRef<Phase>("idle");
  const pausedRef = useRef(false);

  function setPhase(p: Phase) {
    phaseRef.current = p;
    setPhaseState(p);
  }

  function setTurnsBoth(fn: (prev: Turn[]) => Turn[]) {
    turnsRef.current = fn(turnsRef.current);
    setTurns(turnsRef.current);
  }

  // Autoscroll
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight + 400;
  }, [turns, phase]);

  // Keep the screen awake — same rationale as Live/Enroll.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    let released = false;
    const acquire = () => {
      if (released) return;
      navigator.wakeLock?.request("screen")
        .then((l) => { if (!released) lock = l; else l.release(); })
        .catch(() => {});
    };
    acquire();
    const onVis = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVis);
      lock?.release().catch(() => {});
    };
  }, []);

  // Opener on mount — consume the setup screen's pre-warmed request when
  // its selection still matches, otherwise fetch fresh.
  useEffect(() => {
    cancelledRef.current = false;
    playerRef.current = new TtsPlayer();
    void exchange({ prefetched: takeOpener?.() ?? undefined });
    return () => {
      cancelledRef.current = true;
      recorderRef.current?.stop().catch(() => { /* ignore */ });
      playerRef.current?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function history(): { role: "partner" | "learner"; text: string }[] {
    return turnsRef.current.map((t) => ({
      role: t.who === "partner" ? "partner" as const : "learner" as const,
      text: t.text,
    }));
  }

  /** One round trip: optional learner audio / help request → partner reply.
   *  Placeholder bubbles drop in immediately (perceived-latency trick from
   *  the demo) and are patched in place when the server responds. */
  async function exchange(input: { audio?: Blob; helpText?: string; prefetched?: api.LearnTurnHandle }) {
    if (cancelledRef.current) return;
    setPhase("thinking");
    // The learner just spoke (or asked for help) — the previous hint is spent.
    if (input.audio || input.helpText) { setHint(null); setHintShown(false); }
    const learnerPendId = input.audio ? `l-pend-${Date.now()}` : null;
    const partnerPendId = `p-pend-${Date.now()}`;
    setTurnsBoth((prev) => [
      ...prev,
      ...(learnerPendId ? [{
        id: learnerPendId, who: "learner" as const,
        text: "", translit: "", meaning: "", feedback: null, pending: true,
      }] : []),
      {
        id: partnerPendId, who: "partner" as const,
        text: "", translit: "", en: "", showEn: false, audioB64: null, pending: true,
      },
    ]);
    const dropPending = () => setTurnsBoth((prev) =>
      prev.filter((t) => t.id !== learnerPendId && t.id !== partnerPendId));

    const handle = input.prefetched ?? api.learnTurn({
      scenario_id: scenario.id,
      level,
      native_language: native,
      target_language: target,
      history: history(),
      help_text: input.helpText,
      persona_name: personaRef.current ?? undefined,
    }, input.audio);

    let audioB64 = "";
    let gotTurn = false;
    let failed = false;

    await handle.consume({
      // Stage 1: raw ASR echo — fill the learner's bubble the moment
      // recognition finishes, before the LLM/TTS legs return.
      onUser: (user) => {
        if (cancelledRef.current || !learnerPendId || !user) return;
        setTurnsBoth((prev) => prev.map((t) => t.id === learnerPendId
          ? { ...(t as LearnerTurn), text: user.text, translit: user.translit, meaning: user.meaning, pending: false }
          : t));
      },
      // Stage 2: the tutor's textual turn — reply, feedback, hint, persona.
      onTurn: (turn) => {
        if (cancelledRef.current) return;
        gotTurn = true;
        setTurnsBoth((prev) => prev.flatMap((t): Turn[] => {
          if (t.id === learnerPendId) {
            return turn.user ? [{
              id: t.id, who: "learner",
              text: turn.user.text, translit: turn.user.translit, meaning: turn.user.meaning,
              feedback: turn.feedback,
            }] : [];
          }
          if (t.id === partnerPendId) {
            return [{
              id: t.id, who: "partner",
              text: turn.reply_text, translit: turn.reply_translit, en: turn.reply_en,
              showEn: level === "gentle", audioB64: null,
            }];
          }
          return [t];
        }));
        if (turn.persona_name && !personaRef.current) {
          personaRef.current = turn.persona_name;
          setPersonaName(turn.persona_name);
        }
        setHint(turn.suggestion ?? null);
        setHintShown(level === "gentle");
        // Reply text is on screen now; audio follows as the last stage.
        setPhase("partner_speaking");
      },
      // Stage 3: TTS audio — attach to the partner bubble; play after the
      // stream closes (below) so the mic stays gated until playback ends.
      onAudio: (b64) => {
        if (cancelledRef.current) return;
        audioB64 = b64 || "";
        if (b64) setTurnsBoth((prev) => prev.map((t) => t.id === partnerPendId
          ? { ...(t as PartnerTurn), audioB64: b64 } : t));
      },
      onError: (code, message) => {
        if (cancelledRef.current) return;
        failed = true;
        dropPending();
        if (code === 404 || code === 501) setUnavailable(true);
        else setErr(message);
        setPhase("idle");
      },
    });

    if (cancelledRef.current || failed) return;
    if (!gotTurn) { dropPending(); setPhase("idle"); return; }

    setPhase("partner_speaking");
    if (audioB64) {
      try { await playerRef.current?.enqueue(base64ToArrayBuffer(audioB64)); }
      catch { /* ignore */ }
    }
    if (cancelledRef.current) return;
    if (pausedRef.current) { setPhase("idle"); return; }
    void listen();
  }

  async function listen() {
    if (cancelledRef.current || pausedRef.current) return;
    setPhase("listening");
    const rec = new VadRecorder({
      maxSec: ANSWER_MAX_SEC,
      silenceHangFrames: SILENCE_HANG_FRAMES,
      minVoicedFrames: MIN_VOICED_FRAMES,
      onTick: () => {},
      // Cut leading/trailing dead air from the upload — smaller request,
      // faster server-side ASR. (~0.3 s pad kept on each side.)
      trimPadFrames: 10,
    });
    recorderRef.current = rec;
    let samples: Int16Array, voicedSec: number;
    try {
      ({ samples, voicedSec } = await rec.run());
    } catch (e) {
      recorderRef.current = null;
      if (cancelledRef.current) return;
      setErr(e instanceof Error ? e.message : String(e));
      setPhase("idle");
      return;
    }
    recorderRef.current = null;
    if (cancelledRef.current) return;
    if (voicedSec < 0.3) {
      // Nothing usable — quietly re-arm rather than sending silence.
      void listen();
      return;
    }
    talkSecRef.current += voicedSec;
    void exchange({ audio: pcm16ToWavBlob(samples) });
  }

  /** Pause/resume. Paused = no auto-listen after replies, mic torn down.
   *  Un-pausing from idle re-arms the mic; un-pausing mid-reply just lets
   *  the normal after-TTS auto-listen resume. */
  function togglePause() {
    if (pausedRef.current) {
      pausedRef.current = false;
      setPausedState(false);
      if (phaseRef.current === "idle") void listen();
    } else {
      pausedRef.current = true;
      setPausedState(true);
      const rec = recorderRef.current;
      recorderRef.current = null;
      // Abandoned recorder's run() never settles — the old listen() chain
      // goes quiet instead of forwarding a half-captured answer.
      rec?.stop().catch(() => { /* ignore */ });
      if (phaseRef.current === "listening") setPhase("idle");
    }
  }

  /** "Help me say it": ask the tutor for the phrase, spoken and shown. */
  function helpMe() {
    const en = window.prompt("What do you want to say? (in your language)");
    if (!en || !en.trim()) return;
    const rec = recorderRef.current;
    recorderRef.current = null;
    rec?.stop().catch(() => { /* ignore */ });
    void exchange({ helpText: en.trim() });
  }

  function skip() {
    recorderRef.current?.finishNow();
  }

  function finish() {
    cancelledRef.current = true;
    recorderRef.current?.stop().catch(() => { /* ignore */ });
    playerRef.current?.stop();
    const phrases = turnsRef.current.flatMap((t) =>
      t.who === "learner" && t.feedback?.better_phrase
        ? [{ text: t.feedback.better_phrase, translit: t.feedback.better_translit ?? "" }]
        : []);
    onExit({
      turns: turnsRef.current.filter((t) => t.who === "learner").length,
      talkSec: Math.round(talkSecRef.current),
      phrases,
    });
  }

  function replay(t: PartnerTurn) {
    if (!t.audioB64) return;
    try { void playerRef.current?.enqueue(base64ToArrayBuffer(t.audioB64)); }
    catch { /* ignore */ }
  }

  const partnerLabel = personaName ?? scenario.roleShort;
  const statusText =
    paused && phase === "idle" ? "Paused — tap the mic to resume"
    : phase === "thinking" ? `${partnerLabel} is thinking…`
    : phase === "partner_speaking" ? `${partnerLabel} is speaking…`
    : phase === "listening" ? "Your turn — just talk"
    : "";

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0, maxWidth: 720, margin: "0 auto", width: "100%", padding: "22px 28px" }}>
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10, paddingBottom: 14 }}>
        <span className="display" style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>{scenario.title}</span>
        <span className="mono" style={{
          fontSize: 10.5, letterSpacing: 0.6, color: "var(--ink-3)", textTransform: "uppercase",
          background: "var(--surface-2)", borderRadius: "var(--r-pill)", padding: "3px 10px",
        }}>{level} · {langByCode(target, langs).short}</span>
        <div style={{ flex: 1 }} />
        <Btn variant="outline" size="sm" onClick={finish}>End session</Btn>
      </div>

      {unavailable && (
        <div style={{
          flexShrink: 0, background: "color-mix(in oklch, var(--warn) 14%, transparent)",
          color: "var(--ink-2)", padding: "12px 16px", borderRadius: 12, fontSize: 13.5, marginBottom: 10,
        }}>
          Learning practice isn't available on this server yet — the app is ready,
          but the <span className="mono">/learn/turn</span> endpoint hasn't shipped.
        </div>
      )}
      {err && (
        <div style={{
          flexShrink: 0, background: "color-mix(in oklch, var(--bad) 12%, transparent)",
          color: "var(--bad)", padding: "10px 14px", borderRadius: 12, fontSize: 13.5, marginBottom: 10,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {err}
          <button onClick={() => { setErr(null); void listen(); }} style={{
            marginLeft: "auto", background: "transparent", border: "none", cursor: "pointer",
            color: "var(--bad)", fontFamily: "var(--font-ui)", fontWeight: 700, fontSize: 13,
          }}>Retry</button>
        </div>
      )}

      <div ref={scrollRef} className="hc-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18, padding: "4px 2px 8px" }}>
        {turns.length === 0 && !unavailable ? (
          <div style={{ flex: 1, display: "grid", placeItems: "center", color: "var(--ink-3)", fontSize: 14.5 }}>
            {phase === "thinking" ? "Setting the scene…" : ""}
          </div>
        ) : (
          turns.map((t) => t.who === "partner"
            ? <PartnerBubble key={t.id} t={t} persona={partnerLabel} personaRole={scenario.roleDesc}
                onToggleEn={() => setTurnsBoth((prev) => prev.map((x) => (x.id === t.id && x.who === "partner" ? { ...x, showEn: !x.showEn } : x)))}
                onReplay={() => replay(t)} />
            : <LearnerBubble key={t.id} t={t} />)
        )}
      </div>

      {hint && (
        <div style={{ flexShrink: 0, paddingTop: 10, display: "flex", justifyContent: "center" }}>
          {hintShown ? (
            <div style={{
              display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", justifyContent: "center",
              background: "var(--surface-2)", borderRadius: 14, padding: "9px 16px",
              fontSize: 13, color: "var(--ink-3)", maxWidth: 560,
            }}>
              <Icon name="sparkle" size={13} color="var(--accent)" style={{ alignSelf: "center" }} />
              <span>Try:</span>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--ink)", fontStyle: "italic" }}>
                {hint.translit || hint.text}
              </span>
              {hint.translit && <span style={{ color: "var(--ink-2)" }}>{hint.text}</span>}
              {hint.meaning && <span>· "{hint.meaning}"</span>}
            </div>
          ) : (
            <button onClick={() => setHintShown(true)} style={{
              background: "var(--surface-2)", border: "none", cursor: "pointer",
              borderRadius: "var(--r-pill)", padding: "7px 16px",
              display: "inline-flex", alignItems: "center", gap: 7,
              fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 600, color: "var(--ink-3)",
            }}>
              <Icon name="sparkle" size={13} color="var(--accent)" /> Not sure what to say? Show a hint
            </button>
          )}
        </div>
      )}

      <div style={{ flexShrink: 0, paddingTop: 14, display: "flex", justifyContent: "center" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 12, padding: "8px 16px 8px 8px",
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--r-pill)", boxShadow: "var(--shadow-md)", minWidth: "min(460px, 100%)",
        }}>
          <button onClick={togglePause} title={paused ? "Resume" : "Pause"} style={{
            width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
            background: paused ? "var(--surface-2)" : "var(--accent)",
            border: paused ? "1.5px solid var(--border-strong)" : "none", cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            animation: phase === "listening" ? "hc-breathe 1.6s ease-in-out infinite" : "none",
          }}>
            <Icon name={paused ? "mic" : "pause"} size={19} color={paused ? "var(--ink-2)" : "var(--on-accent)"} />
          </button>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-2)", whiteSpace: "nowrap" }}>{statusText}</span>
          <div style={{ flex: 1 }} />
          <Btn variant="tonal" size="sm" onClick={helpMe}>Help me say it</Btn>
          <Btn variant="outline" size="sm" onClick={skip}>Skip</Btn>
        </div>
      </div>
    </div>
  );
}

function PartnerBubble({ t, persona, personaRole, onToggleEn, onReplay }: {
  t: PartnerTurn; persona: string; personaRole: string;
  onToggleEn: () => void; onReplay: () => void;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-start", animation: "hc-pop 0.34s var(--ease)" }}>
      <div style={{ maxWidth: "min(560px, 88%)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--guest-meta)", margin: "0 0 5px 6px" }}>
          {persona}{persona.toLowerCase() === personaRole.toLowerCase() ? "" : ` · ${personaRole}`}
        </div>
        <div style={{ background: "var(--guest-bubble)", borderRadius: "20px 20px 20px 7px", padding: "13px 16px" }}>
          {t.pending ? (
            <div style={{ padding: "4px 2px" }}><Dots color="var(--guest-meta)" /></div>
          ) : (
          <>
          <div style={{ fontSize: 19, lineHeight: 1.45, color: "var(--guest-ink)", fontWeight: 500 }}>{t.text}</div>
          {t.translit && (
            <div style={{ fontSize: 13, color: "var(--guest-meta)", marginTop: 5, fontStyle: "italic" }}>{t.translit}</div>
          )}
          <div style={{
            borderTop: "1px solid color-mix(in oklch, var(--guest) 22%, transparent)",
            marginTop: 10, paddingTop: 8, display: "flex", alignItems: "center", gap: 12,
          }}>
            <button onClick={onToggleEn} style={{
              background: "transparent", border: "none", cursor: "pointer", padding: 0,
              display: "inline-flex", alignItems: "center", gap: 6,
              color: "var(--guest-meta)", fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 600,
            }}>
              <Icon name="eye" size={14} /> {t.showEn ? "Hide English" : "Show English"}
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={onReplay} title="Play again" style={{
              background: "transparent", border: "none", cursor: "pointer", padding: 0,
              display: "inline-flex", color: "var(--guest-meta)",
            }}>
              <Icon name="refresh" size={14} />
            </button>
          </div>
          {t.showEn && (
            <div style={{ fontSize: 14, color: "var(--guest-ink)", opacity: 0.9, marginTop: 8 }}>{t.en}</div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

function LearnerBubble({ t }: { t: LearnerTurn }) {
  const fb = t.feedback;
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", animation: "hc-pop 0.34s var(--ease)" }}>
      <div style={{ maxWidth: "min(560px, 88%)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--you-meta)", margin: "0 6px 5px 0", textAlign: "right" }}>You</div>
        <div style={{ background: "var(--you-bubble)", borderRadius: "20px 20px 7px 20px", padding: "13px 16px" }}>
          {t.pending ? (
            <div style={{ padding: "4px 2px" }}><Dots color="var(--you-meta)" /></div>
          ) : (
          <>
          <div style={{ fontSize: 19, lineHeight: 1.45, color: "var(--you-ink)", fontWeight: 500 }}>{t.text}</div>
          <div style={{ fontSize: 13, color: "var(--you-meta)", marginTop: 5, fontStyle: "italic" }}>
            {t.translit}{t.meaning ? ` · "${t.meaning}"` : ""}
          </div>
          </>
          )}
        </div>
        {fb && (
          <div style={{
            background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12,
            padding: "9px 13px", marginTop: 6,
          }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: fb.understood ? "var(--good)" : "var(--warn)", display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name={fb.understood ? "check" : "volumeX"} size={13} /> {fb.comment}
            </div>
            {fb.better_phrase && (
              <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 4 }}>
                More natural: <span style={{ color: "var(--ink)", fontWeight: 600 }}>{fb.better_phrase}</span>
                {fb.better_translit && <span style={{ fontStyle: "italic", color: "var(--ink-faint)" }}> ({fb.better_translit})</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Recap ────────────────────────────────────────────────────────────────

function Recap({ recap, target, langs, onAgain, onHarder, onScenes }: {
  recap: { turns: number; talkSec: number; phrases: { text: string; translit: string }[] };
  target: string;
  langs: ReturnType<typeof useLanguages>;
  onAgain: () => void; onHarder: () => void; onScenes: () => void;
}) {
  const mins = Math.floor(recap.talkSec / 60);
  const secs = recap.talkSec % 60;
  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "40px 28px 60px" }}>
      <h1 className="display" style={{ fontSize: 26, fontWeight: 700, color: "var(--ink)", margin: 0 }}>
        Nice work
      </h1>
      <p style={{ color: "var(--ink-3)", marginTop: 6, fontSize: 14.5, marginBottom: 20 }}>
        You held a conversation in {langByCode(target, langs).name}.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 22 }}>
        <Stat n={String(recap.turns)} label="turns spoken" />
        <Stat n={`${mins}:${String(secs).padStart(2, "0")}`} label="talking time" />
        <Stat n={String(recap.phrases.length)} label="new phrases" />
      </div>
      {recap.phrases.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)", marginBottom: 9 }}>Your phrase bank</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
            {recap.phrases.map((p, i) => (
              <div key={i} style={{
                background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12,
                padding: "11px 14px", display: "flex", alignItems: "center", gap: 10,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, color: "var(--ink)" }}>{p.text}</div>
                  {p.translit && <div style={{ fontSize: 12, color: "var(--ink-faint)", fontStyle: "italic" }}>{p.translit}</div>}
                </div>
                <Icon name="bookmark" size={16} color="var(--accent)" />
              </div>
            ))}
          </div>
        </>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Btn onClick={onAgain} icon="mic">Keep practicing</Btn>
        <Btn variant="tonal" onClick={onHarder}>Harder level</Btn>
        <Btn variant="outline" onClick={onScenes}>Change scene</Btn>
      </div>
    </div>
  );
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div style={{ background: "var(--surface-2)", borderRadius: 14, padding: "13px 10px", textAlign: "center" }}>
      <div className="display" style={{ fontSize: 21, fontWeight: 700, color: "var(--accent-ink)" }}>{n}</div>
      <div style={{ fontSize: 11.5, color: "var(--ink-faint)", marginTop: 2 }}>{label}</div>
    </div>
  );
}
